import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
    Building2, Phone, Clock, Heart, LogOut, Menu, X, Home, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

// Import modular components
import {
    SiteAreasSection,
    ContactsSection,
    OpeningHoursSection,
    WellnessServicesSection,
    SiteAreaModal,
    ContactModal,
    HoursModal,
    WellnessModal,
    DeleteConfirmModal
} from '../components/manager-welcome';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ManagerWelcomeGuidePage() {
    const navigate = useNavigate();
    const { token, logout, user } = useAuth();
    const [activeSection, setActiveSection] = useState('site-areas');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Data states
    const [siteAreas, setSiteAreas] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [openingHours, setOpeningHours] = useState([]);
    const [wellnessServices, setWellnessServices] = useState([]);
    
    // Modal states
    const [siteAreaModal, setSiteAreaModal] = useState({ open: false, data: null });
    const [contactModal, setContactModal] = useState({ open: false, data: null });
    const [hoursModal, setHoursModal] = useState({ open: false, data: null });
    const [wellnessModal, setWellnessModal] = useState({ open: false, data: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [areasRes, contactsRes, hoursRes, wellnessRes] = await Promise.all([
                axios.get(`${API}/welcome/site-areas`, { headers }),
                axios.get(`${API}/welcome/contacts`, { headers }),
                axios.get(`${API}/welcome/hours`, { headers }),
                axios.get(`${API}/welcome/wellness-services`, { headers })
            ]);
            
            setSiteAreas(areasRes.data);
            setContacts(contactsRes.data);
            setOpeningHours(hoursRes.data);
            setWellnessServices(wellnessRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // CRUD Handlers
    const handleSaveSiteArea = async () => {
        const data = siteAreaModal.data;
        if (!data.title) { toast.error('Please enter a title'); return; }
        const isNew = !data.id || data.id.startsWith('temp-');
        try {
            await axios[isNew ? 'post' : 'put'](isNew ? `${API}/manager/welcome/site-areas` : `${API}/manager/welcome/site-areas/${data.id}`, data, { headers });
            toast.success(`Site area ${isNew ? 'created' : 'updated'}`);
            setSiteAreaModal({ open: false, data: null });
            fetchAllData();
        } catch (error) { toast.error('Failed to save site area'); }
    };

    const handleSaveContact = async () => {
        const data = contactModal.data;
        if (!data.role) { toast.error('Please enter a role/department'); return; }
        const isNew = !data.id || data.id.startsWith('temp-');
        try {
            await axios[isNew ? 'post' : 'put'](isNew ? `${API}/manager/welcome/contacts` : `${API}/manager/welcome/contacts/${data.id}`, data, { headers });
            toast.success(`Contact ${isNew ? 'created' : 'updated'}`);
            setContactModal({ open: false, data: null });
            fetchAllData();
        } catch (error) { toast.error('Failed to save contact'); }
    };

    const handleSaveHours = async () => {
        const data = hoursModal.data;
        if (!data.venue) { toast.error('Please enter a venue name'); return; }
        const isNew = !data.id || data.id.startsWith('temp-');
        try {
            await axios[isNew ? 'post' : 'put'](isNew ? `${API}/manager/welcome/hours` : `${API}/manager/welcome/hours/${data.id}`, data, { headers });
            toast.success(`Opening hours ${isNew ? 'created' : 'updated'}`);
            setHoursModal({ open: false, data: null });
            fetchAllData();
        } catch (error) { toast.error('Failed to save opening hours'); }
    };

    const handleSaveWellness = async () => {
        const data = wellnessModal.data;
        if (!data.title) { toast.error('Please enter a service title'); return; }
        const isNew = !data.id || data.id.startsWith('temp-');
        try {
            await axios[isNew ? 'post' : 'put'](isNew ? `${API}/manager/welcome/wellness-services` : `${API}/manager/welcome/wellness-services/${data.id}`, data, { headers });
            toast.success(`Wellness service ${isNew ? 'created' : 'updated'}`);
            setWellnessModal({ open: false, data: null });
            fetchAllData();
        } catch (error) { toast.error('Failed to save wellness service'); }
    };

    const handleDeleteConfirm = async () => {
        const { type, id } = deleteConfirm;
        const endpoints = {
            'site-area': `${API}/manager/welcome/site-areas/${id}`,
            'contact': `${API}/manager/welcome/contacts/${id}`,
            'hours': `${API}/manager/welcome/hours/${id}`,
            'wellness': `${API}/manager/welcome/wellness-services/${id}`
        };
        try {
            await axios.delete(endpoints[type], { headers });
            toast.success('Item removed');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) { toast.error('Failed to delete'); }
    };

    // Modal openers
    const openNewSiteAreaModal = () => setSiteAreaModal({ open: true, data: { title: '', description: '', icon: 'Building2', display_order: siteAreas.length } });
    const openNewContactModal = () => setContactModal({ open: true, data: { role: '', description: '', phone: '', email: '', availability: 'Mon-Fri, 9am-5pm', display_order: contacts.length } });
    const openNewHoursModal = () => setHoursModal({ open: true, data: { venue: '', weekday_hours: '', weekend_hours: '', display_order: openingHours.length } });
    const openNewWellnessModal = () => setWellnessModal({ open: true, data: { title: '', description: '', price: '', duration_minutes: 60, bookable: true, display_order: wellnessServices.length } });

    // Navigation
    const navItems = [
        { id: 'site-areas', icon: Building2, label: 'Site Areas' },
        { id: 'contacts', icon: Phone, label: 'Key Contacts' },
        { id: 'hours', icon: Clock, label: 'Opening Hours' },
        { id: 'wellness', icon: Heart, label: 'Wellness Services' },
    ];

    const handleLogout = () => { logout(); navigate('/'); };

    return (
        <div className="min-h-screen bg-background flex flex-col md:flex-row" data-testid="manager-welcome-guide-page">
            {/* Mobile Header */}
            <div className="md:hidden sticky top-0 left-0 right-0 bg-teal-700 z-40 p-4 flex items-center justify-between">
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2" data-testid="welcome-mobile-menu-btn">
                    {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <h1 className="text-white font-bold">Welcome Guide CMS</h1>
                <button onClick={handleLogout} className="text-white p-2"><LogOut className="w-5 h-5" /></button>
            </div>

            {/* Mobile Navigation */}
            {sidebarOpen && (
                <div className="md:hidden bg-teal-700 border-b border-white/10 p-4 z-30">
                    <nav className="space-y-1">
                        {navItems.map(item => (
                            <button key={item.id} onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === item.id ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                                data-testid={`welcome-nav-${item.id}`}>
                                <item.icon className="w-5 h-5" /><span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Desktop Sidebar */}
            <aside className="hidden md:flex md:flex-col md:w-64 md:min-h-screen bg-teal-700 flex-shrink-0">
                <div className="p-4 border-b border-white/10">
                    <h1 className="text-xl font-bold text-white">Welcome Guide CMS</h1>
                    <p className="text-white/60 text-sm">{user?.email}</p>
                </div>
                
                <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
                    <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2 px-3">Content Sections</p>
                    {navItems.map(item => (
                        <button key={item.id} onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === item.id ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                            data-testid={`welcome-nav-desktop-${item.id}`}>
                            <item.icon className="w-5 h-5" /><span>{item.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10 space-y-2">
                    <button onClick={() => navigate('/manager')} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                        <ArrowLeft className="w-5 h-5" /><span>Back to Manager Panel</span>
                    </button>
                    <button onClick={() => navigate('/')} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                        <Home className="w-5 h-5" /><span>View Live Site</span>
                    </button>
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors" data-testid="welcome-logout-btn">
                        <LogOut className="w-5 h-5" /><span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-auto">
                <div className="max-w-4xl mx-auto">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {activeSection === 'site-areas' && (
                                <SiteAreasSection siteAreas={siteAreas} onAdd={openNewSiteAreaModal} onEdit={(area) => setSiteAreaModal({ open: true, data: area })} onDelete={(area) => setDeleteConfirm({ open: true, type: 'site-area', id: area.id, name: area.title })} />
                            )}
                            {activeSection === 'contacts' && (
                                <ContactsSection contacts={contacts} onAdd={openNewContactModal} onEdit={(contact) => setContactModal({ open: true, data: contact })} onDelete={(contact) => setDeleteConfirm({ open: true, type: 'contact', id: contact.id, name: contact.role })} />
                            )}
                            {activeSection === 'hours' && (
                                <OpeningHoursSection openingHours={openingHours} onAdd={openNewHoursModal} onEdit={(hours) => setHoursModal({ open: true, data: hours })} onDelete={(hours) => setDeleteConfirm({ open: true, type: 'hours', id: hours.id, name: hours.venue })} />
                            )}
                            {activeSection === 'wellness' && (
                                <WellnessServicesSection wellnessServices={wellnessServices} onAdd={openNewWellnessModal} onEdit={(service) => setWellnessModal({ open: true, data: service })} onDelete={(service) => setDeleteConfirm({ open: true, type: 'wellness', id: service.id, name: service.title })} />
                            )}
                        </>
                    )}
                </div>
            </main>

            {/* Modals */}
            <SiteAreaModal modal={siteAreaModal} setModal={setSiteAreaModal} onSave={handleSaveSiteArea} />
            <ContactModal modal={contactModal} setModal={setContactModal} onSave={handleSaveContact} />
            <HoursModal modal={hoursModal} setModal={setHoursModal} onSave={handleSaveHours} />
            <WellnessModal modal={wellnessModal} setModal={setWellnessModal} onSave={handleSaveWellness} />
            <DeleteConfirmModal deleteConfirm={deleteConfirm} setDeleteConfirm={setDeleteConfirm} onConfirm={handleDeleteConfirm} />
        </div>
    );
}

export default ManagerWelcomeGuidePage;
